import { api } from './client';
import { fetchCompleteCollection } from './pagination';

type ProjectListParams = {
  team_id?: string;
  status?: string;
  search?: string;
  page?: number;
  limit?: number;
};

type CompleteProjectListParams = Omit<ProjectListParams, 'page' | 'limit'>;

const getProjects = (params?: ProjectListParams) =>
  api.get<{
    projects: import('../types').Project[];
    pagination: import('../types').PaginationInfo;
  }>('/projects', params);

// Projects API
export const projectsApi = {
  getAll: getProjects,

  getAllAccessible: async (params?: CompleteProjectListParams) => ({
    projects: await fetchCompleteCollection(async (page, limit) => {
      const response = await getProjects({ ...params, page, limit });
      return { items: response.projects, pagination: response.pagination };
    }),
  }),

  getById: (id: string) =>
    api.get<{
      project: import('../types').Project;
      sections: import('../types').Section[];
      members: import('../types').User[];
      customFields: import('../types').CustomField[];
    }>(`/projects/${id}`),

  create: (data: {
    name: string;
    description?: string;
    team_id: string;
    color?: string;
    due_date?: string;
    is_private?: boolean;
  }) => api.post<{ project: import('../types').Project }>('/projects', data),

  update: (id: string, data: Partial<import('../types').Project>) =>
    api.patch<{ project: import('../types').Project }>(`/projects/${id}`, data),

  delete: (id: string) => api.delete<{ message: string }>(`/projects/${id}`),

  addMember: (projectId: string, userId: string, role?: string) =>
    api.post(`/projects/${projectId}/members`, { user_id: userId, role }),

  updateMember: (projectId: string, userId: string, role: string) =>
    api.patch(`/projects/${projectId}/members/${userId}`, { role }),

  removeMember: (projectId: string, userId: string) =>
    api.delete(`/projects/${projectId}/members/${userId}`),

  createSection: (projectId: string, name: string) =>
    api.post<{ section: import('../types').Section }>(`/projects/${projectId}/sections`, { name }),

  updateSection: (
    projectId: string,
    sectionId: string,
    data: { name?: string; position?: number },
  ) => api.patch(`/projects/${projectId}/sections/${sectionId}`, data),

  deleteSection: (projectId: string, sectionId: string) =>
    api.delete(`/projects/${projectId}/sections/${sectionId}`),

  // Custom fields CRUD
  createCustomField: (
    projectId: string,
    data: { name: string; field_type: string; options?: string | string[] },
  ) =>
    api.post<{ customField: import('../types').CustomField }>(
      `/projects/${projectId}/custom-fields`,
      data,
    ),

  updateCustomField: (
    projectId: string,
    fieldId: string,
    data: { name?: string; options?: string | string[] },
  ) =>
    api.patch<{ customField: import('../types').CustomField }>(
      `/projects/${projectId}/custom-fields/${fieldId}`,
      data,
    ),

  deleteCustomField: (projectId: string, fieldId: string) =>
    api.delete(`/projects/${projectId}/custom-fields/${fieldId}`),
};
